import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseFilterRows, serialiseFilters, type ReportFilterRow } from './reportFilters';

/**
 * Binds a report page's toolbar (search + Filters) to its URL query params
 * (`search`, `filters`, `matchAny`), so a filtered view is a shareable link
 * and reloading the page doesn't lose it.
 *
 * The date-range/branch/device filters this portal already has live
 * separately in `usePortal().filters` (see `features/portal/filters.ts`) —
 * this hook only owns the toolbar's own two params, and is independent of
 * pagination (`page`) and any other params a page keeps in the URL.
 */
export interface UseReportToolbar {
  search: string;
  setSearch: (next: string) => void;
  filterRows: ReportFilterRow[];
  matchAny: boolean;
  applyFilters: (rows: ReportFilterRow[], matchAny: boolean) => void;
}

export function useReportToolbar(): UseReportToolbar {
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get('search') ?? '';
  const filterRows = useMemo(() => parseFilterRows(searchParams.get('filters')), [searchParams]);
  const matchAny = searchParams.get('matchAny') !== 'false';

  const setSearch = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const merged = new URLSearchParams(prev);
          if (next) merged.set('search', next);
          else merged.delete('search');
          // A new search term re-lands on page 1 for a paginated report.
          merged.delete('page');
          return merged;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const applyFilters = useCallback(
    (rows: ReportFilterRow[], nextMatchAny: boolean) => {
      setSearchParams((prev) => {
        const merged = new URLSearchParams(prev);
        const serialised = serialiseFilters(rows);
        if (serialised) merged.set('filters', serialised);
        else merged.delete('filters');
        // Only carried when it differs from the default, so the common case
        // leaves the URL clean.
        if (!nextMatchAny) merged.set('matchAny', 'false');
        else merged.delete('matchAny');
        merged.delete('page');
        return merged;
      });
    },
    [setSearchParams],
  );

  return { search, setSearch, filterRows, matchAny, applyFilters };
}
